import { generateId } from "../../utils/generateId";
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { CookingProgress } from "../../models/cooking-progress";
import { makeSyncUpsert } from "../syncUpsert";

export const cookingProgressAdapter = createEntityAdapter<CookingProgress>();
type CookingProgressState = EntityState<CookingProgress, string>;
const initialState: CookingProgressState = cookingProgressAdapter.getInitialState();

type CookingProgressPayload = Partial<CookingProgress>;

export const cookingProgressSlice = createSlice({
  name: "cookingProgress",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(cookingProgressAdapter),
    addCookingProgress(
      state: CookingProgressState,
      action: PayloadAction<CookingProgressPayload>,
    ): void {
      const cookingProgress = action.payload;

      // Generate Id for cooking progress
      const id: string = generateId();

      // Get current date and time
      const currentDate = new Date().toISOString();

      // Set dates
      const startedAt = currentDate;
      const lastActiveAt = currentDate;

      // Add cooking progress. Payload is Partial<CookingProgress>; callers
      // supply the required fields, so treat the assembled record as complete.
      cookingProgressAdapter.addOne(state, {
        ...cookingProgress,
        id,
        startedAt,
        lastActiveAt,
      } as CookingProgress);
    },
    updateCookingProgress(
      state: CookingProgressState,
      action: PayloadAction<CookingProgressPayload>,
    ): void {
      const { recipeId, ...changes } = action.payload;

      // Target the active session. A recipe can accumulate several records (each
      // "Start Cooking" adds one), so prefer the most-recent in-progress record —
      // matching selectActiveProgressByRecipeId — not just whichever is first.
      const matches = Object.values(state.entities).filter(
        (cp): cp is CookingProgress => cp?.recipeId === recipeId,
      );
      const existing =
        matches
          .filter((cp) => cp.status === "in_progress")
          .sort(
            (a, b) =>
              new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime(),
          )[0] ?? matches[0];

      if (!existing) return;

      cookingProgressAdapter.updateOne(state, {
        id: existing.id,
        changes: {
          ...changes,
          lastActiveAt: new Date().toISOString(),
        },
      });
    },
    // Discard every active session for a recipe (Stop Cooking). Marks all its
    // in-progress records "abandoned" so it leaves Continue Cooking for good,
    // even if more than one session was ever started.
    abandonCookingProgress(
      state: CookingProgressState,
      action: PayloadAction<string>,
    ): void {
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter((cp) => cp?.recipeId === action.payload && cp.status === "in_progress")
        .map((cp) => ({
          id: cp!.id,
          changes: { status: "abandoned" as const, lastActiveAt: now },
        }));
      cookingProgressAdapter.updateMany(state, updates);
    },
  },
});

export const {
  selectAll: selectAllCookingProgress,
  selectById: selectCookingProgressById,
} = cookingProgressAdapter.getSelectors((state: RootState) => state.cookingProgress);

// Return all cooking progresses
export const selectInProgressCookingProgresses = createSelector(
  [selectAllCookingProgress],
  (cookingProgress) => cookingProgress.filter((cp) => cp.status === "in_progress"),
);

// Select most recenty active cook progress for a given recipeId
export const selectActiveProgressByRecipeId = createSelector(
  [selectAllCookingProgress, (_: RootState, recipeId: string) => recipeId],
  (cookingProgress, recipeId) =>
    cookingProgress
      .filter((cp) => cp.recipeId === recipeId && cp.status === "in_progress")
      .sort(
        (a, b) => new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime(),
      )[0] ?? null,
);

export const {
  addCookingProgress,
  updateCookingProgress,
  abandonCookingProgress,
  upsertManyFromSync: upsertCookingProgressFromSync,
} = cookingProgressSlice.actions;

export default cookingProgressSlice.reducer;
